// ══════════════════════════════════════════════════════════════
// COMPASS – Version Check Service
// ══════════════════════════════════════════════════════════════
import { DestroyRef, DOCUMENT, inject, Injectable, NgZone } from '@angular/core';
import { NotificationService } from './notification.service';

/** How often an open tab asks whether a newer version was deployed. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;
/** Coming back to the tab or back online checks at once, but not again within this time of the last check. */
export const VERSION_CHECK_MIN_GAP_MS = 60_000;
/** A check with no answer by then is given up (e.g. a connection left hanging after sleep), so later ones still run. */
export const VERSION_CHECK_TIMEOUT_MS = 10_000;

/**
 * 123y1w8rpab: tells a tab left open across a deploy that a newer version is out, so its user reloads when it suits
 * them (the app never reloads by itself: a half-filled form must not be lost).
 *
 * The version is the ETag nginx gives index.html (its modification time and size): every deploy ships a new
 * index.html, so the ETag changes. The one read at start is the version this tab runs; it is asked again every
 * VERSION_CHECK_INTERVAL_MS, and when the tab is shown again or the browser is back online (at most once per
 * VERSION_CHECK_MIN_GAP_MS), not while it is hidden or offline once the running version is known. A failed check is ignored until the next one; with no
 * ETag at all (the local dev server) nothing is ever shown.
 *
 * A newer version shows one notice, which stays until the user reloads or closes it; no check runs meanwhile.
 * Closed, it does not come back for that version, only for a later one.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly notifications = inject(NotificationService);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** The version this tab runs; null until the first check answers. */
  private running: string | null = null;
  /** The newer version whose notice the user closed: not shown again. */
  private dismissed: string | null = null;
  /** The notice is open: nothing is checked meanwhile. */
  private noticeOpen = false;
  /** index.html came without an ETag: there is nothing to compare, so the checks stop for good. */
  private disabled = false;
  private checking = false;
  /** When the last check started (epoch ms). */
  private lastCheckAt = 0;

  init(): void {
    // Outside Angular: a timer and a HEAD request every few minutes need no change detection.
    this.zone.runOutsideAngular(() => {
      const timer = setInterval(() => void this.check(), VERSION_CHECK_INTERVAL_MS);
      const onVisible = () => {
        if (this.document.visibilityState === 'visible') this.checkSoon();
      };
      const onOnline = () => this.checkSoon();
      const view = this.document.defaultView;
      this.document.addEventListener('visibilitychange', onVisible);
      view?.addEventListener('online', onOnline);
      this.destroyRef.onDestroy(() => {
        clearInterval(timer);
        this.document.removeEventListener('visibilitychange', onVisible);
        view?.removeEventListener('online', onOnline);
      });
      void this.check();
    });
  }

  /** Asks for the current version and shows the notice when it is newer. */
  async check(): Promise<void> {
    if (this.disabled || this.noticeOpen || this.checking) return;
    // The first check always runs, even in a background tab: a deploy before the tab is shown must not become the
    // running version. Offline, it just fails and is tried again.
    if (this.running !== null && (this.document.visibilityState === 'hidden' || !navigator.onLine)) return;
    this.checking = true;
    this.lastCheckAt = Date.now();
    try {
      const version = await this.readVersion();
      if (version === undefined) return;
      if (version === null) {
        // Answered, but nothing to compare by: only the first answer decides (a later one may be a proxy's error page).
        if (this.running === null) this.disabled = true;
        return;
      }
      if (this.running === null) {
        this.running = version;
        return;
      }
      if (version !== this.running && version !== this.dismissed) this.showNotice(version);
    } finally {
      this.checking = false;
    }
  }

  /** A check on returning to the tab or going back online, unless one ran just before. */
  private checkSoon(): void {
    if (Date.now() - this.lastCheckAt >= VERSION_CHECK_MIN_GAP_MS) void this.check();
  }

  /** The ETag of index.html, without the W/ that compression adds; null when there is none, undefined on a failure. */
  private async readVersion(): Promise<string | null | undefined> {
    try {
      // fetch, not HttpClient: no auth header, no 401 logout, no error toasts from the interceptors.
      const response = await fetch(new URL('index.html', this.document.baseURI), {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(VERSION_CHECK_TIMEOUT_MS),
      });
      if (!response.ok) return undefined;
      const etag = response.headers.get('ETag');
      return etag ? etag.replace(/^W\//, '') : null;
    } catch {
      return undefined;
    }
  }

  private showNotice(version: string): void {
    this.zone.run(() => {
      let reloading = false;
      const toast = this.notifications.show({
        type: 'info',
        message: 'COMMON.NEW_VERSION.MESSAGE',
        duration: 0,
        showClose: true,
        action: {
          label: 'COMMON.NEW_VERSION.RELOAD',
          callback: () => {
            reloading = true;
            this.reloadPage();
          },
        },
      });
      if (!toast) return;
      this.noticeOpen = true;
      toast.onHidden.subscribe(() => {
        this.noticeOpen = false;
        // Closed: not again for this version. Reload chosen but the page stayed (the user kept their unsaved changes
        // at the "Leave site?" prompt): the next check shows it again.
        if (!reloading) this.dismissed = version;
      });
    });
  }

  /** A full reload of the current page. Its own method, so specs can stand in for it. */
  protected reloadPage(): void {
    window.location.reload();
  }
}
